import { useState, useRef, useContext } from 'react';
import {
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogContent,
    DialogTitle,
    IconButton,
    Typography,
} from '@mui/material';
import {
    Close as CloseIcon,
    Refresh as RefreshIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { AppContext } from '../sporttia/all';
import { sporttiaColors } from '../theme/theme';

interface TwoFAInputProps {
    text2FA: string;
    onSave: (twoFactorCode: string) => void;
    onClose: () => void;
    onResentCode: () => void;
    loading?: boolean;
    resending?: boolean;
}

export default function Input2FA({
    text2FA,
    onSave,
    onClose,
    onResentCode,
    loading = false,
    resending = false,
}: TwoFAInputProps) {
    const cxt = useContext(AppContext)!;
    const { t } = useTranslation();
    const [code, setCode] = useState<string[]>(Array(6).fill(''));
    const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

    const isCodeComplete = code.every((digit) => digit !== '');

    const handleCodeChange = (index: number, value: string) => {
        if (value !== '' && !/^\d$/.test(value)) {
            cxt.showMessage('E', t('user.invalidDigit'));
            return;
        }

        const newCode = [...code];
        newCode[index] = value;
        setCode(newCode);

        if (value && index < 5) {
            inputRefs.current[index + 1]?.focus();
        }
    };

    const handleKeyDown = (
        index: number,
        e: React.KeyboardEvent<HTMLInputElement>,
    ) => {
        if (e.key === 'Backspace' && !code[index] && index > 0) {
            inputRefs.current[index - 1]?.focus();
        }
        if (e.key === 'Enter' && isCodeComplete) {
            onSave(code.join(''));
        }
    };

    const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
        e.preventDefault();
        const pastedData = e.clipboardData
            .getData('text')
            .replace(/\D/g, '')
            .substring(0, 6)
            .split('');
        const new2FACode = new Array<string>(6).fill('');

        pastedData.forEach((digit: string, index: number) => {
            new2FACode[index] = digit;
        });
        setCode(new2FACode);

        const nextEmptyIndex = new2FACode.findIndex((c) => c === '');
        const focusIndex = nextEmptyIndex === -1 ? 5 : nextEmptyIndex;
        inputRefs.current[focusIndex]?.focus();
    };

    return (
        <Dialog
            open
            onClose={onClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                className: 'rounded-lg',
            }}
        >
            <DialogTitle
                style={{
                    position: 'relative',
                    paddingRight: 48,
                }}
            >
                <Typography variant="h6">{t('user.title2FA')}</Typography>
                <IconButton
                    onClick={onClose}
                    size="small"
                    style={{
                        position: 'absolute',
                        right: 8,
                        top: 8,
                    }}
                >
                    <CloseIcon style={{ width: 18, height: 18 }} />
                </IconButton>
            </DialogTitle>

            <DialogContent>
                <Box className="py-4">
                    {/* Description */}
                    <Typography
                        variant="body2"
                        className="text-center"
                        style={{
                            color: sporttiaColors.text.secondary,
                            marginBottom: 32,
                        }}
                    >
                        {text2FA}
                    </Typography>

                    {/* 6 Input boxes */}
                    <Box className="flex justify-center gap-3 mb-6">
                        {code.map((digit, index) => (
                            <input
                                key={`2fa-input-${String(index)}`}
                                ref={(el: HTMLInputElement | null) => {
                                    inputRefs.current[index] = el;
                                }}
                                name={`code-${String(index)}`}
                                type="text"
                                inputMode="numeric"
                                autoComplete="one-time-code"
                                maxLength={1}
                                value={digit}
                                onChange={(
                                    e: React.ChangeEvent<HTMLInputElement>,
                                ) => {
                                    handleCodeChange(index, e.target.value);
                                }}
                                onKeyDown={(
                                    e: React.KeyboardEvent<HTMLInputElement>,
                                ) => handleKeyDown(index, e)}
                                onPaste={handlePaste}
                                className="w-16 h-16 text-center text-2xl font-semibold border rounded-lg bg-white transition-colors duration-200 outline-none"
                                style={{
                                    borderColor: digit
                                        ? sporttiaColors.primary.dark
                                        : '#d1d5db',
                                    ...(digit
                                        ? {
                                              boxShadow: `0 0 0 1px ${sporttiaColors.primary.dark}`,
                                          }
                                        : {}),
                                }}
                                onFocus={(e) => {
                                    e.target.style.borderColor =
                                        sporttiaColors.primary.dark;
                                    e.target.style.boxShadow = `0 0 0 1px ${sporttiaColors.primary.dark}`;
                                }}
                                onBlur={(e) => {
                                    if (!digit) {
                                        e.target.style.borderColor = '#d1d5db';
                                        e.target.style.boxShadow = 'none';
                                    }
                                }}
                            />
                        ))}
                    </Box>

                    {/* Buttons */}
                    <Box className="flex gap-3 justify-center flex-wrap">
                        <Button
                            variant="contained"
                            onClick={() => onSave(code.join(''))}
                            disabled={!isCodeComplete || loading}
                            startIcon={
                                loading ? (
                                    <CircularProgress
                                        size={18}
                                        color="inherit"
                                    />
                                ) : undefined
                            }
                            className="rounded-md normal-case px-6"
                            style={{
                                backgroundColor:
                                    isCodeComplete && !loading
                                        ? sporttiaColors.primary.main
                                        : undefined,
                                color:
                                    isCodeComplete && !loading
                                        ? sporttiaColors.primary.contrastText
                                        : undefined,
                            }}
                        >
                            <span>
                                {loading
                                    ? t('user.verifying2FA')
                                    : t('user.accept2FA')}
                            </span>
                        </Button>

                        <Button
                            variant="outlined"
                            onClick={() => onResentCode()}
                            disabled={resending}
                            startIcon={
                                resending ? (
                                    <CircularProgress
                                        size={18}
                                        color="inherit"
                                    />
                                ) : (
                                    <RefreshIcon />
                                )
                            }
                            className="rounded-md normal-case px-6"
                            color="inherit"
                        >
                            <span>
                                {resending
                                    ? t('user.resendingCode')
                                    : t('user.resendCode').toUpperCase()}
                            </span>
                        </Button>

                        <Button
                            variant="outlined"
                            onClick={onClose}
                            startIcon={<CloseIcon />}
                            className="rounded-md normal-case px-6"
                            color="error"
                        >
                            <span>{t('general.close').toUpperCase()}</span>
                        </Button>
                    </Box>
                </Box>
            </DialogContent>
        </Dialog>
    );
}
